import { Injectable, OnDestroy } from '@angular/core';
import { Logger } from './logger.service';
import { User } from '../entity/user.entity';
import { BehaviorSubject, Observable, Subscription, tap, timer } from 'rxjs';
import { AuthService } from './auth.service';
import { plainToInstance } from 'class-transformer';
import { UserApiService } from './user-api.service';

const CURRENT_USER_KEY = 'currentUser';

@Injectable({
  providedIn: 'root',
})
export class CurrentUserService implements OnDestroy {
  private readonly logger = new Logger(CurrentUserService.name);
  private currentUserSubject: BehaviorSubject<User | null>;
  public currentUser$: Observable<User | null>;
  private authSubscription: Subscription;
  private isFetchingUser = false;
  private lastFetchTimestamp = 0;
  private readonly FETCH_THROTTLE_MS = 500; // Minimum time between API calls

  constructor(
    private authService: AuthService,
    private userApiService: UserApiService
  ) {
    const storedUser = localStorage.getItem(CURRENT_USER_KEY);
    const initialUser = storedUser ? JSON.parse(storedUser) : null;

    this.currentUserSubject = new BehaviorSubject<User | null>(
      plainToInstance(User, initialUser)
    );
    this.currentUser$ = this.currentUserSubject.asObservable();

    this.authSubscription = this.authService.isAuthenticated$.subscribe({
      next: isAuthenticated => {
        if (isAuthenticated) {
          this.fetchCurrentUser().subscribe({
            error: error => {
              this.logger.error('Error fetching current user', error);
            },
          });
        } else {
          this.clearCurrentUser();
        }
      },
      error: error => {
        this.logger.error('Error in auth subscription', error);
      },
    });
  }

  ngOnDestroy(): void {
    this.authSubscription.unsubscribe();
  }

  public get currentUserValue(): User | null {
    return this.currentUserSubject.value;
  }

  public setCurrentUser(user: User | null): void {
    this.currentUserSubject.next(user);
    if (user) {
      localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(CURRENT_USER_KEY);
    }
  }

  public clearCurrentUser(): void {
    this.currentUserSubject.next(null);
    localStorage.removeItem(CURRENT_USER_KEY);
  }

  public fetchCurrentUser(): Observable<User> {
    const now = Date.now();
    const timeSinceLastFetch = now - this.lastFetchTimestamp;

    // If already fetching or if the last fetch was too recent, return the current user as an observable
    if (
      this.isFetchingUser ||
      (this.currentUserValue && timeSinceLastFetch < this.FETCH_THROTTLE_MS)
    ) {
      return new Observable<User>(observer => {
        if (this.currentUserValue) {
          observer.next(this.currentUserValue);
          observer.complete();
        } else {
          // Wait a bit for the ongoing fetch to complete
          timer(100).subscribe({
            next: () => {
              if (this.currentUserValue) {
                observer.next(this.currentUserValue);
              } else {
                // If still no value, make the request anyway
                this.makeUserRequest().subscribe({
                  next: user => observer.next(user),
                  error: error => observer.error(error),
                  complete: () => observer.complete(),
                });
              }
              observer.complete();
            },
            error: error => {
              this.logger.error('Error in timer', error);
              observer.error(error);
            },
          });
        }
      });
    }

    return this.makeUserRequest();
  }

  private makeUserRequest(): Observable<User> {
    this.isFetchingUser = true;
    this.lastFetchTimestamp = Date.now();

    return this.userApiService.fetchCurrentUser().pipe(
      tap({
        next: user => {
          this.setCurrentUser(plainToInstance(User, user));
          this.isFetchingUser = false;
        },
        error: () => {
          this.isFetchingUser = false;
        },
      })
    );
  }

  public getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  public patchCurrentUser(user: Partial<User>): Observable<User> {
    return this.userApiService.patchCurrentUser(user).pipe(
      tap(updatedUser => {
        const currentUser = this.currentUserValue;
        this.setCurrentUser(
          plainToInstance(User, {
            ...(currentUser as any),
            ...(updatedUser as any),
          })
        );
      })
    );
  }
}
